import { Vue } from 'vue-property-decorator';
import mapSeries from 'async/mapSeries';
import range from 'lodash/range';

function getInitialState() {
  return {
    loadingFormInstanceByModuleAndSubject: {},
    loadedRepositoryForm: false,
    userCanAddForms: false,
    currentForm: null,
    createdForm: null,
    pagination: null,
    loading: false,
    instances: {},
    saving: false,
    forms: {},
  };
}

export const RepositoryForms = {
  namespaced: true,

  state: getInitialState(),

  getters: {
    getCurrentForm: ({ currentForm }) => currentForm,

    getInstanceForModuleAndSubject:
      ({ instances }) =>
      (module, subject) => {
        return instances?.[module]?.[subject] ?? null;
      },

    getRepositoryForms: (state) => (uuid) => {
      return state.forms[uuid] ?? [];
    },

    isUserAllowedToCreate: ({ userCanAddForms }) => userCanAddForms,

    areFormsLoaded: (state) => (uuid) => {
      return Object.keys(state.forms).includes(uuid);
    },

    areFormsLoading: (state) => state.loading,

    isSomeFormInstanceLoading: ({ loadingFormInstanceByModuleAndSubject }) =>
      Object.values(loadingFormInstanceByModuleAndSubject).some((v) => v),

    getPaginationData:
      ({ pagination }) =>
      (uuid) =>
        pagination?.[uuid] ?? {},

    loadedRepositoryForm: ({ loadedRepositoryForm }) => loadedRepositoryForm,

    createdForm: ({ createdForm }) => createdForm,
  },

  mutations: {
    removeForm(state, removedForm) {
      let uuid = Object.keys(removedForm)[0];
      state.forms[uuid] = state.forms[uuid].filter((form) => form.uuid !== removedForm[uuid]);
    },

    setFormDataInstanceByModuleAndSubject(state, { module, subject, value }) {
      Vue.set(state.loadingFormInstanceByModuleAndSubject, `${module}.${subject}`, value);
    },

    setFormInstanceForModuleAndSubject(state, { module, subject, instance }) {
      let moduleInstances = state.instances[module] ?? {};

      moduleInstances = { ...moduleInstances, [subject]: instance };

      state.instances = { ...state.instances, [module]: moduleInstances };
    },

    setRepositoryClean(state, uuid) {
      state.dirty = state.dirty.filter((item) => item !== uuid);
    },

    setPagination(state, pagination) {
      state.pagination = { ...state.pagination, ...pagination };
    },

    setLoadedRepositoryForm(state, bool) {
      Vue.set(state, 'loadedRepositoryForm', bool);
    },

    resetRepositoryForm(state) {
      Vue.set(state, 'loadedRepositoryForm', false);
      Vue.set(state, 'currentForm', null);
    },

    updateRepositoryForms(state, newForms) {
      state.forms = { ...state.forms, ...newForms };
    },

    resetRepositoryForms(state, repositoryUuid) {
      Vue.delete(state.forms, repositoryUuid);
    },

    setAddPermission(state, allowed) {
      state.userCanAddForms = allowed;
    },

    setFormDetails(state, formData) {
      state.currentForm = formData;
    },

    setSaving(state, saving) {
      state.saving = saving;
    },

    setLoading(state, loading) {
      state.loading = loading;
    },

    resetState(state) {
      Object.assign(state, getInitialState());
    },

    setCreatedForm(state, form) {
      Vue.set(state, 'createdForm', form);
    },
  },

  actions: {
    async loadFormDetails({ commit }, { repository, form, version }) {
      if (!repository || !form) {
        return;
      }

      commit('setLoading', true);
      commit('setLoadedRepositoryForm', false);

      try {
        const response = await this._vm.$api('repositories.forms.show', {
          version: '1.0',
          repository,
          form,
          v: version,
        });

        const formData = response.data;
        commit('setFormDetails', formData);
      } catch (err) {
        console.error(err);
      }

      commit('setLoading', false);
      commit('setLoadedRepositoryForm', true);
    },

    async loadFormInstance(
      { commit },
      { repository, module, subject, project, event, pipeline, version, run_version }
    ) {
      commit('setFormDataInstanceByModuleAndSubject', { module, subject, value: true });
      try {
        const response = await this._vm.$api('repositories.forms.instance', {
          version: '1.0',
          repository,
          project,
          pipeline,
          event,
          module,
          subject,
          v: version,
          run_version,
        });

        const instance = response.data.data;
        commit('setFormInstanceForModuleAndSubject', { module, subject, instance });
      } catch (err) {
        console.error(err);
      }

      commit('setFormDataInstanceByModuleAndSubject', { module, subject, value: false });
    },

    async loadRepositoryForms(
      { commit },
      { repository, filter, page, perPage, sortBy, sortDesc, allPages, signedOnly }
    ) {
      if (!repository) {
        return;
      }

      let forms = {};
      let pagination = {};
      const loadAllPages = allPages ?? false;

      commit('setLoading', true);
      try {
        let fetchParams = {
          version: '1.0',
          repository,
          filter,
          page,
          perPage,
          sortBy,
          sortDesc,
          ...(signedOnly && { signed_only: signedOnly }),
        };

        const response = await this._vm.$api('repositories.forms.index', fetchParams);

        forms = response.data.data;
        pagination = { ...response.data.meta, ...response.data.links };
        commit('setAddPermission', response.data.can?.add);
        if (loadAllPages && pagination.last_page > pagination.current_page) {
          const pageRange = range(pagination.current_page, pagination.last_page);
          const remainingForms = await mapSeries(pageRange, async (currentPage, callback) => {
            try {
              const pageResponse = await this._vm.$api('repositories.forms.index', {
                ...fetchParams,
                page: currentPage + 1,
              });
              callback(null, pageResponse.data?.data ?? []);
            } catch (err) {
              callback(err, []);
            }
          });

          let appendForms = remainingForms.reduce((acc, resultsPage) => [...acc, ...resultsPage], []);

          forms = [...forms, ...appendForms];
        }
      } catch (err) {
        console.error(err);
      }

      commit('setPagination', { [repository]: pagination });
      commit('updateRepositoryForms', { [repository]: forms });

      commit('setLoading', false);
    },

    async createForm({ commit }, { repository, form }) {
      const name = form.name;
      const description = form.description;

      commit('setSaving', true);

      const response = await this._vm.$api(
        'repositories.forms.store',
        {
          version: '1.0',
          repository,
        },
        {
          name,
          description,
        }
      );

      commit('setCreatedForm', response?.data?.data);

      commit('setSaving', false);
    },

    async updateForm({ commit }, { repository, form, newData }) {
      const description = newData.description;
      const name = newData.name;

      commit('setSaving', true);

      await this._vm.$api(
        'repositories.forms.update',
        {
          version: '1.0',
          repository,
          form: form.uuid,
        },
        {
          name,
          description,
        }
      );

      commit('setSaving', false);
    },

    async copyForm({ commit }, { repository, form, newData }) {
      const { name: newFormName, version: formVersion } = newData;

      const payload = {
        formVersion,
      };
      const request = {
        form: form.uuid,
        version: '1.0',
        repository,
      };

      if (form.name !== newFormName) {
        payload.form_name = newFormName;
      }

      commit('setSaving', true);

      await this._vm.$api('repositories.forms.copy', request, payload);

      commit('setSaving', false);
    },

    async deleteForm({ commit }, { repository, form, comment }) {
      commit('setSaving', true);

      await this._vm.$api(
        'repositories.forms.destroy',
        {
          version: '1.0',
          repository,
          form,
        },
        { comment }
      );
      commit('removeForm', { [repository]: form });
      commit('setSaving', false);
    },

    updateRepositoryForms({ commit }, { uuid, forms }) {
      commit('updateRepositoryForms', { [uuid]: forms });
    },

    clearRepositoryForms({ commit }, repositoryUuid) {
      commit('resetRepositoryForms', repositoryUuid);
    },

    resetRepositoryFormsState({ commit }) {
      commit('resetState');
    },
  },
};
